import React from "react";
import { Pressable, ScrollView, StyleSheet, View } from "react-native";
import { Keyboard } from "lucide-react";
import CustomText from "../../../common/general/CustomText/CustomText";
import { colors, radii, spacing, typographyScale, webTransitions } from "../../../../styles/design-system";

interface MathButton {
  label: string;
  latex: string;
  title: string;
}

const MATH_TEMPLATES: MathButton[] = [
  { label: "a/b", latex: "\\frac{#0}{#1}", title: "Fraction" },
  { label: "x\u00B2", latex: "#0^{#1}", title: "Exponent" },
  { label: "\u221Ax", latex: "\\sqrt{#0}", title: "Square root" },
  { label: "x\u2081", latex: "#0_{#1}", title: "Subscript" },
  { label: "\u03A3", latex: "\\sum_{#0}^{#1}", title: "Summation" },
  { label: "\u222B", latex: "\\int_{#0}^{#1}", title: "Integral" },
  { label: "( )", latex: "\\left(#0\\right)", title: "Parentheses" },
];

const MATH_SYMBOLS: MathButton[] = [
  { label: "\u03C0", latex: "\\pi", title: "Pi" },
  { label: "\u03B8", latex: "\\theta", title: "Theta" },
  { label: "\u03B1", latex: "\\alpha", title: "Alpha" },
  { label: "\u03B2", latex: "\\beta", title: "Beta" },
  { label: "\u221E", latex: "\\infty", title: "Infinity" },
  { label: "\u00B1", latex: "\\pm", title: "Plus-minus" },
  { label: "\u2264", latex: "\\leq", title: "Less than or equal" },
  { label: "\u2265", latex: "\\geq", title: "Greater than or equal" },
  { label: "\u2260", latex: "\\neq", title: "Not equal" },
];

interface MathKeyboardPaletteProps {
  onInsert: (latex: string) => void;
  onToggleVirtualKeyboard?: () => void;
  /** When true, the virtual keyboard toggle button shows as active (keyboard is open). */
  isVirtualKeyboardOpen?: boolean;
}

const MathKeyboardPalette: React.FC<MathKeyboardPaletteProps> = ({
  onInsert,
  onToggleVirtualKeyboard,
  isVirtualKeyboardOpen = false,
}) => {
  return (
    <View style={styles.container}>
      <View style={styles.row}>
        <CustomText text="Templates" useTranslationText={false} style={styles.rowLabel} />
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.buttonsScroll}>
          {MATH_TEMPLATES.map((t) => (
            <Pressable
              key={t.label}
              onPress={() => onInsert(t.latex)}
              accessibilityLabel={`Insert ${t.title}`}
              accessibilityRole="button"
              style={(state) => {
                const hovered = (state as { hovered?: boolean }).hovered;
                return [
                  styles.templateButton,
                  hovered && styles.templateButtonHovered,
                  state.pressed && styles.buttonPressed,
                ];
              }}>
              <CustomText text={t.label} useTranslationText={false} style={styles.templateButtonText} />
            </Pressable>
          ))}
        </ScrollView>
      </View>

      <View style={styles.row}>
        <CustomText text="Symbols" useTranslationText={false} style={styles.rowLabel} />
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.buttonsScroll}>
          {MATH_SYMBOLS.map((s) => (
            <Pressable
              key={s.label}
              onPress={() => onInsert(s.latex)}
              accessibilityLabel={`Insert ${s.title}`}
              accessibilityRole="button"
              style={(state) => {
                const hovered = (state as { hovered?: boolean }).hovered;
                return [
                  styles.symbolButton,
                  hovered && styles.symbolButtonHovered,
                  state.pressed && styles.buttonPressed,
                ];
              }}>
              <CustomText text={s.label} useTranslationText={false} style={styles.symbolButtonText} />
            </Pressable>
          ))}
        </ScrollView>

        {onToggleVirtualKeyboard && (
          <Pressable
            onPress={onToggleVirtualKeyboard}
            accessibilityLabel="Toggle virtual keyboard"
            accessibilityRole="button"
            style={(state) => {
              const hovered = (state as { hovered?: boolean }).hovered;
              return [
                styles.keyboardButton,
                isVirtualKeyboardOpen && styles.keyboardButtonActive,
                hovered && styles.keyboardButtonHovered,
                state.pressed && styles.buttonPressed,
              ];
            }}>
            <Keyboard size={18} color={isVirtualKeyboardOpen ? colors.brand[600] : colors.grey[500]} />
          </Pressable>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderTopWidth: 1,
    borderTopColor: colors.grey[100],
    gap: spacing.xxs,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
  },
  rowLabel: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    color: colors.grey[400],
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginRight: spacing.xxs,
  },
  buttonsScroll: {
    flexDirection: "row",
    gap: spacing.xs,
    alignItems: "center",
    flex: 1,
  },
  templateButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: radii.lg,
    backgroundColor: colors.grey[100],
    ...webTransitions.fast,
  },
  templateButtonHovered: {
    backgroundColor: colors.grey[200],
  },
  templateButtonText: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontFamily: "monospace",
    color: colors.grey[700],
  },
  symbolButton: {
    width: 32,
    height: 32,
    borderRadius: radii.md,
    alignItems: "center",
    justifyContent: "center",
    ...webTransitions.fast,
  },
  symbolButtonHovered: {
    backgroundColor: colors.grey[100],
  },
  symbolButtonText: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    color: colors.grey[700],
  },
  keyboardButton: {
    width: 32,
    height: 32,
    borderRadius: radii.md,
    alignItems: "center",
    justifyContent: "center",
    marginRight: spacing.xs,
    ...webTransitions.fast,
  },
  keyboardButtonActive: {
    backgroundColor: colors.brand[100],
  },
  keyboardButtonHovered: {
    backgroundColor: colors.grey[100],
  },
  buttonPressed: {
    opacity: 0.8,
  },
});

export default MathKeyboardPalette;
